<template>
  <div class="flex flex-col items-center">
    <div class="flex justify-center space-x-3 mt-4">
      <input
          v-for="(digit, index) in code" @paste.prevent="handlePaste"
          :key="index"
          ref="inputs"
          v-model="code[index]"
          class="w-10 h-10 text-center text-2xl font-semibold rounded-lg border transition-all outline-none"
          :class="errors.length ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-[#4A148C]'"
          type="text"
          maxlength="1"
          pattern="[0-9]"
          inputmode="numeric"
          autocomplete="off"
          @input="handleInput(index, $event)"
          @keydown.delete="handleDelete(index, $event)"
          @blur="validateCode"
      />
    </div>

    <div v-if="errors.length" class="mt-2 space-y-1">
      <span v-for="(error, index) in errors" :key="index" class="block text-xs text-red-600">
        {{ error }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  required: {
    type: Boolean,
    default: true
  },
  errors: {
    type: Array,
    default: () => []
  }
});

const emits = defineEmits(['update:modelValue', 'update:errors']);

const code = ref(new Array(6).fill(''));
const inputs = ref([]);

watch(
    () => props.modelValue,
    (newValue) => {
      if (newValue.length === 6) {
        code.value = newValue.split('');
      }
    }
);

function handleInput(index, event) {
  const val = event.target.value.replace(/\D/g, '');
  if (val) {
    code.value[index] = val;
    if (index < 5) {
      nextTick(() => inputs.value[index + 1]?.focus());
    }
  }
  emits('update:modelValue', code.value.join(''));
  emits('update:errors', []);
}

function handleDelete(index, event) {
  if (event.key === 'Backspace' && !code.value[index] && index > 0) {
    nextTick(() => inputs.value[index - 1]?.focus());
  }
}

function validateCode() {
  if (props.required && code.value.join('').length < 6) {
    emits('update:errors', ['Please enter a 6-digit verification code.']);
  } else {
    emits('update:errors', []);
  }
}

function handlePaste(event) {
  const pasted = event.clipboardData.getData('text').trim();
  const digits = pasted.replace(/\D/g, '').split('').slice(0, code.value.length);
  code.value = code.value.map((_, i) => digits[i] || '');
  emits('update:modelValue', code.value.join(''));
  emits('update:errors', []);
  const focusIndex = digits.length < code.value.length ? digits.length : code.value.length - 1;
  nextTick(() => inputs.value[focusIndex]?.focus());
}

</script>
